"use client";
import { useCallback } from "react";

import { useEventLocationContext } from "@stadium/services/analytics/context/event-location";
import useActionEvents from "@stadium/services/analytics/hooks/use-action-events";

import { getEventName } from "@stadium/helpers/analytics/utils";

import { ANALYTICS_EVENT_ACTION_CLICKED } from "@stadium/services/analytics/constants/default";

/**
 * Returns `(handler, eventLocationAnalytics) => wrappedHandler` that tracks then calls the handler.
 * The wrapped handler forwards all arguments to `handler`.
 * `eventLocationAnalytics`: `{ eventProperties, target, action, track }`.
 */
export const useWithAnalytics = () => {
  const {
    location: eventLocation,
    services,
    baseEventLocationProperties,
  } = useEventLocationContext();
  const { trackEvent } = useActionEvents(services);

  return useCallback(
    (handler, eventLocationAnalytics) =>
      (...args) => {
        if (eventLocation && eventLocationAnalytics?.track) {
          trackEvent({
            eventName: getEventName({
              targetLocation: eventLocation,
              target: eventLocationAnalytics?.target || "",
              action: eventLocationAnalytics?.action ?? ANALYTICS_EVENT_ACTION_CLICKED,
            }),
            eventProperties: {
              ...baseEventLocationProperties,
              ...eventLocationAnalytics?.eventProperties,
            },
          });
        }
        return handler?.(...args);
      },
    [eventLocation, trackEvent, baseEventLocationProperties],
  );
};

export default useWithAnalytics;
